import React, { FC, Key, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Space, Spin } from 'antd';
import styles from './index.module.scss';
import ResizeObserver from 'rc-resize-observer';
import { Dayjs } from 'dayjs';
import { EnergyUsageCostContext } from '..';
import { debounce, isNil, omit } from 'lodash-es';
import tittleDecoration from '@/assets/images/tittleDecoration.png';
import { EchartsWithTheme, Empty, Form, Modal, Select, Tabs, Themes, useTheme, Tooltip } from '@maxtropy/components';
import {
  V2EnergyMediumPhysicalUnitByUnitGroupPostResponse,
  apiV2EnergyUnitBoardDetailEnergyMediumInEnergyUnitPost,
  apiV2EnergyUnitTotalCostPost,
  V2EnergyUnitTotalCostPostResponse,
  apiV2EnergyUnitElectricCostPost,
  V2EnergyUnitElectricCostPostResponse,
  apiV2EnergyUnitMediumCostPost,
  V2EnergyUnitMediumCostPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { EnergyUnitItem } from '@/api/energyUnitDashboard';
import SwitchRangePicker from '../EnergyAnalysisOverview/components/SwitchRangePicker';
import { DateType } from '../prop';
import { DataPeriodListItem, getDataQueryPeriodList } from '@/api/energyOverview';
import CostBanner from './components/CostBanner';
import { getBaseOption, getElectricOption, getTotalOption } from './options';
import ElectricCard from './components/ElectricCard';
import { InfoCircleOutlined } from '@ant-design/icons';
import { EchartsWithThemeRef } from '@maxtropy/components/es/components/EchartsWithTheme';
import { formatThousandUnit } from '@/utils/utils';
import SubordinateCost from './SubordinateCost';
import SubordinateCostRank from './SubordinateCostRank';
import { useSearchParams } from 'react-router-dom';

export type RangeValue = [Dayjs | null, Dayjs | null] | null;

export interface BaseInfoProps extends Omit<V2EnergyUnitElectricCostPostResponse, 'chartList'> {}
export type TotalCostChartProps = V2EnergyUnitTotalCostPostResponse['chartList'];
export type ElectricCostChartProps = V2EnergyUnitElectricCostPostResponse['chartList'];
const text = `用能成本数据不包含今日的成本数据；且电能-用能成本统计为电度电费统计（仅计算的成本为零售交易电费，计算方式为尖峰平谷电量*对应尖峰平谷时段单价），其中不包含基本电费以及附加电费等。`;

interface TabItemProps {
  label: string;
  key: string;
  sceneId: Key;
}
interface UnitDetailProps {
  unitPath?: string;
  times?: RangeValue;
  defaultData: EnergyUnitItem[];
  energyUnitList?: V2EnergyMediumPhysicalUnitByUnitGroupPostResponse['data'];
}
const UnitDetail: FC<UnitDetailProps> = ({ unitPath, defaultData, energyUnitList }) => {
  const theme = useTheme();
  const { unitId, groupId, hasChildren } = useContext(EnergyUsageCostContext);
  const [tabItems, setTabItems] = useState<TabItemProps[]>([]);
  const [activeKey, setActiveKey] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [chartWidth, setChartWidth] = useState<number>();
  const [timeResolution, setTimeResolution] = useState<DateType>(DateType.DAY);
  const [time, setTime] = useState<RangeValue>();
  const [chartData, setChartData] = useState<V2EnergyUnitMediumCostPostResponse>();

  const [baseInfo, setBaseInfo] = useState<BaseInfoProps>();
  const [totalCostChartData, setTotalCostChartData] = useState<V2EnergyUnitTotalCostPostResponse>();
  const [electricCostChartData, setElectricCostChartData] = useState<V2EnergyUnitElectricCostPostResponse>();

  const [dataQueryPeriodId, setDataQueryPeriodId] = useState<number>();
  const [dataPeriodList, setDataPeriodList] = useState<DataPeriodListItem[]>([]);
  const [form] = Form.useForm();

  const debouncedRunnerRef = useRef<ReturnType<typeof debounce>>();
  const chartRef = useRef<EchartsWithThemeRef>();

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const routeActiveKey = urlSearchParams.get('activeKey') ?? undefined;

  useEffect(() => {
    if (!time || !timeResolution || !time[0] || !time[1]) return;
    setDataQueryPeriodId(undefined);
    form.setFieldValue('dataQueryPeriodId', undefined);
    getDataQueryPeriodList({ startTime: time?.[0]?.valueOf(), endTime: time?.[1]?.valueOf() }).then(res => {
      setDataPeriodList(res.dataPeriodList ?? []);
    });
  }, [timeResolution, time, unitId, groupId]);

  function findEnergyUnitIdById(id: number, data: EnergyUnitItem[]): number | null {
    for (let unit of data) {
      if (unit.energyUnitId === id) {
        return unit.energyUnitId;
      }
      if (unit.children && unit.children.length > 0) {
        const foundId: number | null = findEnergyUnitIdById(id, unit.children);
        if (foundId) {
          return foundId;
        }
      }
    }
    return null;
  }
  // 获取能源介质
  useEffect(() => {
    if (isNil(unitId)) return;
    if (isNil(findEnergyUnitIdById(unitId, defaultData))) {
      return;
    }
    apiV2EnergyUnitBoardDetailEnergyMediumInEnergyUnitPost({ energyUnitId: String(unitId) })
      .onError(error => {
        setTabItems([]);
        setActiveKey(undefined);
        const { errorMessage } = error?.cause;
        Modal.error({
          title: errorMessage,
        });
        throw error;
      })
      .then(res => {
        const items = (res.list ?? []).map(item => ({
          label: item.energyName,
          key: String(item.id),
          sceneId: item.sceneId,
        }));
        if (items.length) {
          items.unshift({
            label: '总成本',
            key: 'total',
            sceneId: 0,
          });
        }
        setTabItems(items as TabItemProps[]);
        if (routeActiveKey && items.find(k => k.key === routeActiveKey)) {
          setActiveKey(routeActiveKey);
        } else {
          const defaultKey = items.find(item => item.key === activeKey)
            ? activeKey
            : isNil(items?.[0]?.key)
            ? undefined
            : String(items?.[0]?.key);
          setActiveKey(routeActiveKey ?? defaultKey);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId, defaultData]);

  if (!debouncedRunnerRef.current) {
    debouncedRunnerRef.current = debounce((params, activeKey) => {
      switch (activeKey) {
        case 'total':
          apiV2EnergyUnitTotalCostPost(params)
            .then(res => {
              setBaseInfo(omit(res ?? {}, ['chartList']));
              setTotalCostChartData(res ?? {});
            })
            .finally(() => setLoading(false));
          break;
        case '2030':
          apiV2EnergyUnitElectricCostPost({ ...params, energyMediumId: activeKey })
            .then(res => {
              setBaseInfo(omit(res ?? {}, ['chartList']));
              setElectricCostChartData(res ?? {});
            })
            .finally(() => setLoading(false));
          break;
        default:
          apiV2EnergyUnitMediumCostPost({ ...params, energyMediumId: activeKey })
            .then(res => {
              setBaseInfo(omit(res ?? {}, ['chartList']));
              setChartData(res ?? {});
            })
            .finally(() => setLoading(false));
          break;
      }
    }, 300);
  }

  useEffect(() => {
    if (!unitId || !activeKey || !timeResolution || !time || !time[0] || !time[1]) return;
    const energyName = tabItems?.find(item => String(item.key) === activeKey)?.label;
    const params = {
      fromTime: time[0].valueOf(),
      toTime: time[1].valueOf(),
      timeResolution,
      energyUnitId: unitId,
      energyUnitGroupId: groupId,
      energyMediumName: energyName,
      periodId: dataQueryPeriodId,
    };

    setLoading(true);
    debouncedRunnerRef.current!(params, activeKey);

    return () => {
      debouncedRunnerRef.current?.cancel();
    };
  }, [unitId, activeKey, time, dataQueryPeriodId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const _option = useMemo(() => {
    switch (activeKey) {
      case 'total':
        return getTotalOption(totalCostChartData ?? {}, timeResolution, theme);
      case '2030':
        return getElectricOption(electricCostChartData ?? {}, timeResolution, theme);
      default:
        return getBaseOption(chartData!, timeResolution, theme);
    }
  }, [totalCostChartData, electricCostChartData, chartData, activeKey]);

  return (
    <div className={styles.unitDetail}>
      {tabItems.length > 0 ? (
        <>
          <div className={styles.tabWrapper}>
            <Tabs
              selectedLinePlacement="top"
              onChange={key => {
                setActiveKey(key);
                setChartData({});
                setElectricCostChartData({});
                setTotalCostChartData({});
                setDataQueryPeriodId(undefined);
                form.setFieldValue('dataQueryPeriodId', undefined);
              }}
              activeKey={activeKey}
              items={tabItems}
              bordered
              tabBarGutter={20}
            ></Tabs>
          </div>
          <Form form={form} isOrigin={true} className={styles.panel}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div className={styles.title}>
                <span>当前选择</span>
                <span>{unitPath}</span>
              </div>
              <Space size={16}>
                <SwitchRangePicker
                  time={time}
                  setTime={setTime}
                  setTimeResolution={setTimeResolution}
                  setDataQueryPeriodId={setDataQueryPeriodId}
                  timeResolution={timeResolution}
                />
              </Space>

              <div className={styles.headerWrapper}>
                <img src={tittleDecoration} alt="pic" className={styles.titleBorder} />
                <span className={styles.title}>用能成本统计</span>
              </div>
              <CostBanner baseInfo={baseInfo} />
              {activeKey === '2030' && <ElectricCard baseInfo={baseInfo} />}
              <Space size={16}>
                <Form.Item name="dataQueryPeriodId" label="数据查询时段" style={{ marginBottom: 0 }}>
                  <Select
                    placeholder="请选择"
                    style={{ width: 240 }}
                    onChange={(value: number) => setDataQueryPeriodId(value)}
                    options={dataPeriodList.map(item => ({ value: item.id, label: item.name }))}
                    allowClear
                  />
                </Form.Item>
                <Tooltip placement="right" title={text}>
                  <div>
                    <InfoCircleOutlined
                      style={{ fontSize: '14px', marginRight: '6px', color: 'var(--mx-warning-color)' }}
                    />
                    <span className={styles.statisText} style={{ color: 'var(--mx-text-desc-color)' }}>
                      用能成本数据不包含今日的成本数据；且电能-用能成本统计为电度电费统计（仅计算的成本为零售交易电费，计算方式为尖峰平谷电量*对应尖峰平谷时段单价），其中不包含基本电费以及附加电费等。
                    </span>
                  </div>
                </Tooltip>
              </Space>
              <ResizeObserver onResize={({ width }) => setChartWidth(width)}>
                <Spin spinning={loading}>
                  <EchartsWithTheme
                    empty={{
                      isShowEmpty: !(
                        (
                          totalCostChartData?.chartList ??
                          electricCostChartData?.chartList ??
                          chartData?.chartList ??
                          []
                        ).length > 0
                      ),
                      emptyNode: <Empty style={{ height: 400, paddingTop: 160 }} />,
                    }}
                    style={{ height: 400, width: chartWidth }} // 样式的设置width与height
                    theme={theme}
                    option={_option}
                    ref={d => {
                      if (d) {
                        chartRef.current = d;
                      }
                    }}
                    onEvents={{
                      brushSelected: (params: any) => {
                        let startTime;
                        let endTime;
                        let totalCost = 0; // 总成本
                        const brushComponent = params.batch[0];
                        const chartObject = chartRef.current?.getInstance();
                        const option = chartObject?.getOption();
                        const series = option?.series as any[];
                        const xAxis = (option?.xAxis as any[])[0]?.data;

                        const rich: any = {};
                        const lines: string[] = [];

                        brushComponent.selected.forEach((item: any) => {
                          const findSeries = series?.find(i => i.name === item.seriesName);

                          let arr: any[] = [];
                          item.dataIndex.forEach((c: number, index: number) => {
                            if (index === 0) {
                              startTime = xAxis[c];
                            }
                            if (index === item.dataIndex.length - 1) {
                              endTime = xAxis[c];
                            }
                            arr.push(findSeries.data[c].value);

                            // totalCost 在遍历时直接累加，排除 isPeriod
                            if (!findSeries.data[c].isPeriod && !isNil(findSeries.data[c].value)) {
                              totalCost += Number(findSeries.data[c].value);
                            }
                          });

                          // 当前系列总和
                          const subtotal = arr.filter(x => !isNil(x)).reduce((a, b) => Number(a) + Number(b), 0);

                          let color: string;

                          // 如果 series 自己有颜色
                          if (findSeries?.itemStyle?.color) {
                            color = findSeries.itemStyle.color as string;
                          } else {
                            const colors = Array.isArray(option!.color) ? option!.color : [option!.color];
                            color = colors[item.seriesIndex % colors.length] as string;
                          }

                          // 定义 rich 样式
                          const markerKey = `m${item.seriesIndex}`;
                          rich[markerKey] = {
                            height: 10,
                            width: 10,
                            backgroundColor: color,
                            borderRadius: 5,
                          };

                          // 拼接带 marker 的文本
                          lines.push(`{${markerKey}|} ${item.seriesName}：${formatThousandUnit(subtotal)} 元`);
                        });

                        if (brushComponent.areas.length > 0) {
                          const chartWidth = chartObject?.getWidth?.() || 0;
                          let left = brushComponent.areas[0].range[1] - 340; // 你原来的定位方式
                          // 防止 title 超出右边界
                          if (left + 340 > chartWidth) {
                            left = chartWidth - 340 - 10; // 240是title宽度，10是边距
                          }
                          if (left < 0) {
                            left = 0; // 防止超出左边界
                          }
                          chartObject?.setOption({
                            title: {
                              show: true,
                              padding: 8,
                              backgroundColor: theme === Themes.DARK ? 'rgba(0,0,0,0.8)' : '#fff',
                              borderColor: 'transparent',
                              borderRadius: 2,
                              textStyle: {
                                color: theme === Themes.DARK ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
                                fontSize: 12,
                                fontWeight: 500,
                                lineHeight: 20,
                                rich,
                              },
                              text: [
                                `${startTime} ~ ${endTime}`,
                                `总成本：${formatThousandUnit(totalCost)} 元`,
                                ...lines,
                              ].join('\n'),
                              top: 100,
                              left,
                              zlevel: 99999,
                            },
                          });
                        } else {
                          chartObject?.setOption({
                            title: {
                              show: false,
                            },
                          });
                        }
                      },
                    }}
                  />
                </Spin>
              </ResizeObserver>
            </Space>
            {hasChildren && (
              <>
                <SubordinateCost
                  timeResolution={timeResolution}
                  time={time}
                  energyUnitId={unitId}
                  energyUnitGroupId={groupId}
                  energyMediumId={activeKey}
                />
                <SubordinateCostRank
                  timeResolution={timeResolution}
                  time={time}
                  energyUnitId={unitId}
                  energyUnitGroupId={groupId}
                  energyMediumId={activeKey}
                />
              </>
            )}
          </Form>
        </>
      ) : (
        <Empty style={{ paddingTop: '10%' }} />
      )}
    </div>
  );
};

export default UnitDetail;
